import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PublicNav from "../Components/PublicNav";
import Footer from "../Components/Footer";

const Icon = {
  sparkles: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2l1.09 3.26L13.5 6l-3.41 1.09L9 10 7.91 6.74 4.5 6l3.41-1.09L9 2z"/><path d="M17 13l.55 1.64L19 15l-1.45.36L17 17l-.55-1.64L15 15l1.45-.36L17 13z"/></svg>,
  barChart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
  trendingUp: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  tag: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  checkCircle: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  bell: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  mapPin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  layers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
};

const FEATURES = [
  {
    icon: "users", featured: true,
    title: "Competitor Intelligence",
    desc: "See exactly where competitors outperform you — which themes they're praised for, their rating trajectory, and the gaps you can exploit.",
    example: "\"Competitor A mentioned 40% more for 'fast service' and 'cleanliness'\"",
  },
  {
    icon: "barChart",
    title: "Complaint Clustering",
    desc: "AI groups recurring complaints into themes — you see '23 mentions: slow service' instead of reading 200 reviews one by one.",
    example: "\"32% of negative reviews mention wait times during lunch\"",
  },
  {
    icon: "sparkles",
    title: "AI Reply Drafts",
    desc: "Generate professional, friendly, or apologetic responses in seconds — edit and post directly to Google with one click.",
    example: "\"Cut reply time from 3 days to under 3 minutes\"",
  },
  {
    icon: "tag",
    title: "Feature Request Extraction",
    desc: "Spot recurring asks in review text — online booking, parking, loyalty cards — so you know exactly what to build or fix next.",
    example: "\"14 customers asked for online ordering this month\"",
  },
  {
    icon: "trendingUp",
    title: "Rating & Sentiment Trend",
    desc: "Dual-axis charts show your average rating and % positive reviews month-by-month — spot exactly when things improved or dropped.",
    example: "\"Detect a 0.4★ drop before it damages your ranking\"",
  },
  {
    icon: "bell",
    title: "Instant Bad Review Alerts",
    desc: "Get email alerts the moment a 1–2★ review lands — with the full review text and a direct reply link so you respond before it spreads.",
    example: "\"Reply within minutes, not days\"",
  },
  {
    icon: "checkCircle",
    title: "Response Rate Tracking",
    desc: "See exactly what % of reviews you've replied to. Businesses that respond consistently earn 35% more customer trust signals.",
    example: "\"Businesses replying to 80%+ of reviews average 4.6★\"",
  },
  {
    icon: "calendar",
    title: "Weekly Intelligence Digest",
    desc: "Every Monday: avg rating, positive %, top complaint keywords, response rate — your whole reputation in one email.",
    example: "\"Your full week at a glance, every Monday morning\"",
  },
  {
    icon: "download",
    title: "Export & Share Reports",
    desc: "Download filtered review data as CSV or PDF. Perfect for team meetings, agency reports, or client presentations.",
    example: "\"Share actionable insights with your team instantly\"",
  },
  {
    icon: "layers",
    title: "Franchise Tracker",
    desc: "Track up to 5 branch locations side by side — compare ratings, sentiment, and complaint patterns across every site from one view.",
    example: "\"Branch 3 is 0.6★ below your best location — here's why\"",
  },
];

const PLANS_MONTHLY = [
  {
    id: "starter", label: "Starter", price: "Free", period: "",
    features: ["5 searches per month", "Basic rating summary", "Star breakdown chart", "No credit card required"],
  },
  {
    id: "monthly", label: "Pro", price: "$9.99", period: "/ mo", featured: true,
    features: ["Unlimited searches", "AI reply drafts (3 tones)", "Sentiment analysis", "Competitor benchmarking", "Keyword trend tracking", "Export CSV & PDF", "Cancel anytime"],
  },
  {
    id: "yearly", label: "Business", price: "$24.99", period: "/ mo",
    features: ["Everything in Pro", "Email alerts for bad reviews", "Weekly digest emails", "Multi-location switching", "Response rate tracking", "Priority support"],
  },
];

const PLANS_YEARLY = [
  {
    id: "starter", label: "Starter", price: "Free", period: "",
    features: ["5 searches per month", "Basic rating summary", "Star breakdown chart", "No credit card required"],
  },
  {
    id: "monthly", label: "Pro", price: "$7.99", period: "/ mo", note: "billed $95.88/yr", featured: true,
    features: ["Unlimited searches", "AI reply drafts (3 tones)", "Sentiment analysis", "Competitor benchmarking", "Keyword trend tracking", "Export CSV & PDF", "Cancel anytime"],
  },
  {
    id: "yearly", label: "Business", price: "$19.99", period: "/ mo", note: "billed $239.88/yr",
    features: ["Everything in Pro", "Email alerts for bad reviews", "Weekly digest emails", "Multi-location switching", "Response rate tracking", "Priority support"],
  },
];

const BIZ_TYPES = ["restaurants", "salons", "hotels", "gyms", "dental clinics", "retail stores", "law firms", "spas", "resorts", "cafes", "auto repair shops", "physiotherapy clinics", "real estate agencies", "barber shops", "veterinary clinics"];

const BIZ_PILLS  = ["Restaurants", "Cafes", "Hotels", "Salons", "Gyms", "Dental Clinics", "Auto Repair", "Retail Stores", "Law Firms", "Spas", "Resorts", "Barber Shops", "Physiotherapy", "Real Estate", "Vet Clinics"];

const PLATFORMS = [
  { id: "google",     name: "Google Reviews", live: true,  abbr: "G",   bg: "#e8f0fe", color: "#4285f4" },
  { id: "play",       name: "Play Store",     live: false, abbr: "▶",   bg: "#e6f4ea", color: "#01875f" },
  { id: "appstore",   name: "App Store",      live: false, abbr: "A",   bg: "#e3f1fd", color: "#0071e3" },
  { id: "trustpilot", name: "Trustpilot",     live: false, abbr: "★",   bg: "#e6f9f1", color: "#00b67a" },
];

const DEMO_COMPLAINTS  = [["slow service", 23], ["limited seating", 17], ["pricing", 11], ["parking", 8]];
const DEMO_POSITIVES   = [["friendly staff", 54], ["great espresso", 41], ["cosy atmosphere", 36], ["quick service", 22]];
const DEMO_REQUESTS    = [["online ordering", 14], ["loyalty card", 9], ["wifi password", 7], ["vegan options", 6]];

function useCountUp(target, duration, active) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cur = 0;
    const steps = Math.ceil(duration / 16);
    const inc = target / steps;
    const t = setInterval(() => {
      cur += inc;
      if (cur >= target) { setVal(target); clearInterval(t); }
      else setVal(Math.floor(cur));
    }, 16);
    return () => clearInterval(t);
  }, [active, target, duration]);
  return val;
}

function StatItem({ prefix = "", target, suffix = "", label, active }) {
  const val = useCountUp(target, 1400, active);
  return (
    <div className="lp-stat">
      <span className="lp-stat-num">{prefix}{val}{suffix}</span>
      <span className="lp-stat-label">{label}</span>
    </div>
  );
}


function LandingPage() {
  const navigate = useNavigate();

  // Typing effect
  const [typeIdx,   setTypeIdx]   = useState(0);
  const [typeText,  setTypeText]  = useState("");
  const [deleting,  setDeleting]  = useState(false);

  // Stats counter
  const statsRef      = useRef(null);
  const [statsOn, setStatsOn] = useState(false);

  // Pricing toggle
  const [isYearly, setIsYearly] = useState(false);

  // Typing effect
  useEffect(() => {
    const word = BIZ_TYPES[typeIdx];
    if (!deleting && typeText === word) {
      const t = setTimeout(() => setDeleting(true), 2000);
      return () => clearTimeout(t);
    }
    if (deleting && typeText === "") {
      setDeleting(false);
      setTypeIdx((i) => (i + 1) % BIZ_TYPES.length);
      return;
    }
    const t = setTimeout(
      () => setTypeText(deleting ? typeText.slice(0, -1) : word.slice(0, typeText.length + 1)),
      deleting ? 55 : 100
    );
    return () => clearTimeout(t);
  }, [typeText, deleting, typeIdx]);

  // Stats intersection
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStatsOn(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Scroll reveal
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("lp-visible"); }),
      { threshold: 0.08 }
    );
    document.querySelectorAll(".lp-animate").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const plans = isYearly ? PLANS_YEARLY : PLANS_MONTHLY;

  return (
    <div className="lp-page">
      <PublicNav />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">

          <div className="lp-hero-text">
            <div className="lp-hero-badge">
              Built for&nbsp;<span className="lp-type-word">{typeText}</span>
              <span className="lp-type-cursor">|</span>
            </div>
            <h1 className="lp-hero-title">
              Stop guessing what customers want.<br />
              <span className="lp-hero-accent">RIRA tells you.</span>
            </h1>
            <p className="lp-hero-sub">
              RIRA is the <strong>AI-powered review intelligence platform</strong> for local businesses.
              Surface complaint patterns, competitor gaps, and AI-drafted replies —
              all from your Google reviews, in under 60 seconds.
            </p>
            <div className="lp-hero-btns">
              <button className="lp-btn-primary" onClick={() => navigate("/auth?tab=register")}>
                Start for free
              </button>
              <button className="lp-btn-ghost" onClick={() => navigate("/auth")}>
                Sign in
              </button>
            </div>
            <div className="lp-hero-trust">
              <span>No credit card required</span>
              <span className="lp-trust-dot" />
              <span>Setup in 3 minutes</span>
              <span className="lp-trust-dot" />
              <span>Cancel anytime</span>
            </div>
          </div>

          <div className="lp-hero-visual">
            <div className="lp-mockup-glow" />
            <div className="lp-rira-card">

              {/* RIRA header bar */}
              <div className="lp-rc-header">
                <div className="lp-rc-brand">
                  <svg width="18" height="18" viewBox="0 0 36 36" fill="none"><rect width="36" height="36" rx="9" fill="#1A1A1A"/><polyline points="6,27 12,17 19,22 28,10" stroke="white" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="28" cy="10" r="2.8" fill="white"/></svg>
                  <span>Rira</span>
                </div>
                <div className="lp-rc-biz-row">
                  <span className="lp-rc-biz-name">The Coffee House</span>
                  <span className="lp-rc-rating">★ 4.2</span>
                  <span className="lp-rc-live-dot" />
                  <span className="lp-rc-live-label">Live</span>
                </div>
              </div>

              {/* Sentiment chips */}
              <div className="lp-rc-sentiment">
                <span className="lp-rc-sent lp-rc-sent--pos">78% positive</span>
                <span className="lp-rc-sent lp-rc-sent--neg">8% negative</span>
                <span className="lp-rc-sent lp-rc-sent--neu">128 reviews</span>
              </div>

              {/* Top Insights */}
              <div className="lp-rc-section-label">Top Insights</div>
              <div className="lp-rc-insight-row">
                <span className="lp-rc-chip lp-rc-chip--bad">slow service <b>23</b></span>
                <span className="lp-rc-chip lp-rc-chip--bad">parking <b>8</b></span>
                <span className="lp-rc-chip lp-rc-chip--good">friendly staff <b>54</b></span>
                <span className="lp-rc-chip lp-rc-chip--good">espresso <b>41</b></span>
              </div>

              {/* Star Breakdown */}
              <div className="lp-rc-section-label">Star Breakdown</div>
              <div className="lp-rc-stars">
                {[["5★", 68, "#22c55e"], ["4★", 18, "#86efac"], ["3★", 6, "#fbbf24"], ["2★", 4, "#f97316"], ["1★", 4, "#ef4444"]].map(([label, pct, color]) => (
                  <div key={label} className="lp-rc-star-row">
                    <span className="lp-rc-star-label">{label}</span>
                    <div className="lp-rc-star-track">
                      <div className="lp-rc-star-fill" style={{ width: `${pct}%`, background: color }} />
                    </div>
                    <span className="lp-rc-star-pct">{pct}%</span>
                  </div>
                ))}
              </div>

              {/* Response Rate */}
              <div className="lp-rc-section-label">Response Rate</div>
              <div className="lp-rc-resp-row">
                <div className="lp-rc-resp-stat">
                  <span className="lp-rc-resp-num">73%</span>
                  <span className="lp-rc-resp-lbl">Replied</span>
                </div>
                <div className="lp-rc-resp-stat">
                  <span className="lp-rc-resp-num">34</span>
                  <span className="lp-rc-resp-lbl">Unreplied</span>
                </div>
                <div className="lp-rc-resp-stat">
                  <span className="lp-rc-resp-num">2.1d</span>
                  <span className="lp-rc-resp-lbl">Avg response</span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* ── Stats bar ─────────────────────────────────────── */}
      <div className="lp-stats-bar" ref={statsRef}>
        <StatItem prefix="" target={10000} suffix="+"  label="Reviews analyzed"       active={statsOn} />
        <StatItem prefix="< " target={5}   suffix="s"  label="Avg AI reply time"      active={statsOn} />
        <StatItem prefix="" target={10}    suffix="+"  label="Intelligence signals"   active={statsOn} />
      </div>
      <div className="lp-stats-dots">
        <span className="lp-stats-dot" />
        <span className="lp-stats-dot" />
        <span className="lp-stats-dot" />
      </div>

      {/* ── Platforms bar ─────────────────────────────────── */}
      <div className="lp-platforms-bar">
        <div className="lp-platforms-bar-inner">
          <span className="lp-platforms-label">Supported platforms</span>
          <div className="lp-platforms-list">
            {PLATFORMS.map((p) => (
              <div key={p.id} className={`lp-plat-item ${!p.live ? "lp-plat-item--soon" : ""}`}>
                <span className="lp-plat-icon" style={{ background: p.bg, color: p.color }}>{p.abbr}</span>
                <span className="lp-plat-name">{p.name}</span>
                {p.live
                  ? <span className="lp-plat-badge lp-plat-live">Live</span>
                  : <span className="lp-plat-badge lp-plat-soon">Soon</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Features ──────────────────────────────────────── */}
      <section className="lp-section lp-features-section">
        <div className="lp-section-inner">
          <div className="lp-section-label lp-animate">Intelligence Features</div>
          <h2 className="lp-section-title lp-animate">10 signals. One dashboard.</h2>
          <p className="lp-section-sub lp-animate">Everything a local business owner needs to compete on reputation — and win.</p>
          <div className="lp-features-grid">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className={`lp-feature-card lp-animate ${f.featured ? "lp-feature-card--hero" : ""}`}
                style={{ transitionDelay: `${(i % 3) * 80}ms` }}
              >
                {f.featured && <div className="lp-feature-hero-badge">★ Differentiator</div>}
                <div className="lp-feature-icon">{Icon[f.icon]}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
                {f.example && <div className="lp-feature-example">{f.example}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What you'll know ──────────────────────────────── */}
      <section className="lp-section lp-know-section">
        <div className="lp-section-inner">
          <div className="lp-section-label lp-animate">Actionable intelligence</div>
          <h2 className="lp-section-title lp-animate">4 critical questions. Answered instantly.</h2>
          <p className="lp-section-sub lp-animate">These decisions usually take weeks of manual reading. RIRA surfaces them the moment you log in.</p>
          <div className="lp-know-grid">
            {[
              { q: "What are customers complaining about?",        a: "\"32% of your 1–3★ reviews mention slow service during lunch hours.\"",                           color: "#dc2626", bg: "#fff1f2" },
              { q: "Why are competitors winning?",                 a: "\"Competitor A mentioned 40% more for 'fast service' and 'clean space'.\"",                       color: "#7c3aed", bg: "#f5f3ff" },
              { q: "What do customers want built or changed?",     a: "\"14 customers asked for online ordering. 9 mentioned a loyalty programme.\"",                    color: "#d97706", bg: "#fffbeb" },
              { q: "Is my reputation improving or declining?",     a: "\"Rating up 0.4★ over 3 months. Positive sentiment +12% since new staff joined.\"",              color: "#059669", bg: "#f0fdf4" },
            ].map(({ q, a, color, bg }) => (
              <div key={q} className="lp-know-card lp-animate" style={{ borderLeftColor: color }}>
                <div className="lp-know-q">{q}</div>
                <div className="lp-know-a" style={{ background: bg, color }}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────── */}
      <section className="lp-section lp-how-section">
        <div className="lp-section-inner">
          <div className="lp-section-label lp-animate">How it works</div>
          <h2 className="lp-section-title lp-animate">Up and running in seconds</h2>
          <div className="lp-steps">
            {[
              { num: "01", title: "Enter a business name",   desc: "Type any business name to pull in its customer reviews instantly." },
              { num: "02", title: "Get AI-powered insights", desc: "See sentiment breakdown, rating trends, keyword patterns — all in one view." },
              { num: "03", title: "Respond and improve",     desc: "Draft replies with one click, export reports, and track progress over time." },
            ].map((s, i) => (
              <div key={s.num} className="lp-step lp-animate" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="lp-step-num">{s.num}</div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── See it in action ──────────────────────────────── */}
      <section className="lp-section lp-demo-section">
        <div className="lp-section-inner">
          <div className="lp-section-label lp-animate">See it in action</div>
          <h2 className="lp-section-title lp-animate">Real insights. Instantly.</h2>
          <p className="lp-section-sub lp-animate">What RIRA surfaces from 143 Google reviews for a local coffee shop.</p>
          <div className="lp-demo-grid">

            {/* AI Summary */}
            <div className="lp-demo-card lp-demo-card--wide lp-animate">
              <div className="lp-demo-card-tag">AI Summary</div>
              <p className="lp-demo-summary-text">
                Customers consistently praise the <strong>friendly staff</strong> and <strong>quality espresso</strong>, particularly the cosy atmosphere. The most common complaints centre on{" "}
                <span className="lp-demo-warn">slow service during peak hours</span> and{" "}
                <span className="lp-demo-warn">limited seating</span>. Positive sentiment has risen <strong>12%</strong> over the past 3 months.
              </p>
              <div className="lp-demo-chips">
                <span className="lp-demo-chip lp-demo-chip--pos">78% Positive</span>
                <span className="lp-demo-chip lp-demo-chip--neu">14% Neutral</span>
                <span className="lp-demo-chip lp-demo-chip--neg">8% Negative</span>
              </div>
            </div>

            {/* Top Complaints */}
            <div className="lp-demo-card lp-animate">
              <div className="lp-demo-card-tag lp-demo-tag--bad">Top Complaints</div>
              <div className="lp-demo-list">
                {DEMO_COMPLAINTS.map(([label, n]) => (
                  <div key={label} className="lp-demo-list-item">
                    <span className="lp-demo-list-label">{label}</span>
                    <span className="lp-demo-badge lp-demo-badge--bad">{n}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Positives */}
            <div className="lp-demo-card lp-animate">
              <div className="lp-demo-card-tag lp-demo-tag--good">Top Positives</div>
              <div className="lp-demo-list">
                {DEMO_POSITIVES.map(([label, n]) => (
                  <div key={label} className="lp-demo-list-item">
                    <span className="lp-demo-list-label">{label}</span>
                    <span className="lp-demo-badge lp-demo-badge--good">{n}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Feature Requests */}
            <div className="lp-demo-card lp-animate">
              <div className="lp-demo-card-tag lp-demo-tag--req">Customers Want</div>
              <div className="lp-demo-list">
                {DEMO_REQUESTS.map(([label, n]) => (
                  <div key={label} className="lp-demo-list-item">
                    <span className="lp-demo-list-label">{label}</span>
                    <span className="lp-demo-badge lp-demo-badge--req">{n}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Reply Draft */}
            <div className="lp-demo-card lp-demo-card--wide lp-animate">
              <div className="lp-demo-card-tag">AI Reply Draft</div>
              <div className="lp-demo-review">
                <span className="lp-demo-review-stars">★★★</span>
                <span className="lp-demo-review-text">"Good coffee but I waited 20 min during lunch hour and my order was wrong..."</span>
                <span className="lp-demo-review-author">— Mike T.</span>
              </div>
              <div className="lp-demo-reply">
                "Thank you for visiting, Mike! We sincerely apologise for the wait and the mix-up — lunch hours are our busiest time and we're actively adding staff to improve speed and accuracy. We hope to welcome you back soon for a smoother experience! ☕"
              </div>
              <div className="lp-demo-tones">
                <span className="lp-demo-tone lp-demo-tone--active">Friendly</span>
                <span className="lp-demo-tone">Professional</span>
                <span className="lp-demo-tone">Apologetic</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Who uses Rira ─────────────────────────────────── */}
      <section className="lp-section lp-biztype-section">
        <div className="lp-section-inner lp-animate">
          <div className="lp-section-label">Built for</div>
          <h2 className="lp-section-title">Local business owners who compete on reputation</h2>
          <p className="lp-section-sub">You work hard to deliver great service. RIRA makes sure your reviews reflect it — and tells you exactly when they don't.</p>
          <div className="lp-biztype-pills">
            {BIZ_PILLS.map((b) => <span key={b} className="lp-biztype-pill">{b}</span>)}
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────── */}
      <section className="lp-section lp-pricing-section">
        <div className="lp-section-inner">
          <div className="lp-section-label lp-animate">Pricing</div>
          <h2 className="lp-section-title lp-animate">Simple, honest pricing</h2>
          <p className="lp-section-sub lp-animate">No hidden fees. Cancel anytime.</p>

          {/* Billing toggle */}
          <div className="lp-billing-toggle lp-animate">
            <span className={`lp-toggle-label ${!isYearly ? "lp-toggle-label--active" : ""}`}>Monthly</span>
            <button
              className={`lp-toggle-switch ${isYearly ? "lp-toggle-switch--on" : ""}`}
              onClick={() => setIsYearly((y) => !y)}
              aria-label="Toggle billing period"
            >
              <span className="lp-toggle-knob" />
            </button>
            <span className={`lp-toggle-label ${isYearly ? "lp-toggle-label--active" : ""}`}>
              Yearly&nbsp;<span className="lp-toggle-save">Save 20%</span>
            </span>
          </div>

          <div className="lp-pricing-cards">
            {plans.map((p, i) => (
              <div
                key={p.id}
                className={`lp-plan-card lp-animate ${p.featured ? "lp-plan-card--featured" : ""}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {p.featured && <div className="lp-plan-badge">Most popular</div>}
                <div className="lp-plan-label">{p.label}</div>
                <div className="lp-plan-price">
                  <span className="lp-plan-amount">{p.price}</span>
                  <span className="lp-plan-period">{p.period}</span>
                </div>
                {p.note && <div className="lp-plan-equiv">{p.note}</div>}
                <ul className="lp-plan-features">
                  {p.features.map((f) => (
                    <li key={f}><span className="lp-plan-check">&#10003;</span> {f}</li>
                  ))}
                </ul>
                <button
                  className={`lp-plan-cta ${p.featured ? "lp-plan-cta--featured" : ""}`}
                  onClick={() => navigate("/auth?tab=register")}
                >
                  Get started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA banner ────────────────────────────────────── */}
      <section className="lp-cta-section lp-animate">
        <div className="lp-cta-inner">
          <h2 className="lp-cta-title">Your reviews are already telling you what to fix.</h2>
          <p className="lp-cta-sub">Sign up free and get your first insights in under 60 seconds — no credit card needed.</p>
          <button className="lp-cta-btn" onClick={() => navigate("/auth?tab=register")}>
            Start for free — see insights in 60 seconds
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default LandingPage;
